import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { InternalChargeCardAccount } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
} from 'expo-router';
import useAccountDetails from 'features/accounts/hooks/useAccountDetails';
import { useToggleSafePay } from 'features/believe/learningCenter/hooks/useToggleSafePay';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import StepChecklist from 'shared/components/vxl/ProgressIndicator/StepChecklist/StepChecklist';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import HyperlinkSmText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkSmText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import ActionScreen from 'shared/patterns/vxl/ActionScreen/ActionScreen';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const SNACKBAR_DURATION = 5000;

const HowBelieveWorksScreen = () => {
  const { toggleSafePay, loading, errorModel, response } = useToggleSafePay();
  const { accountId } = useLocalSearchParams<{ accountId: string }>();
  const { bottom } = useSafeAreaInsets();

  const {
    refetch,
    accountDetails,
    loading: fetching,
  } = useAccountDetails(accountId);

  const isEnabled = (accountDetails as InternalChargeCardAccount)
    ?.isSafePayEnabled;

  useEffect(() => {
    if (response) {
      refetch?.();
    }
  }, [refetch, response]);

  const { t: translatedText } = useTranslation('believe', {
    keyPrefix: 'learningCenter',
  });

  const bottomSheetRef = useRef<BottomSheetModal>(null);
  const handleLinkTextAnalytics = usePressableAnalytics({
    analyticsId: 'linkText',
    customAttributes: {
      faq_name: 'safe_pay',
    },
  });

  const handleSafePayOnButtonAnalytics = usePressableAnalytics({
    analyticsId: 'safePayOn',
  });
  const handleLinkText = () => {
    handleLinkTextAnalytics();
    bottomSheetRef?.current?.present();
  };
  const [buttonClicked, setButtonClicked] = useState(false);

  const handleTurnSafePayOn = () => {
    setButtonClicked(true);
    handleSafePayOnButtonAnalytics();
    toggleSafePay(true);
    bottomSheetRef?.current?.close();
  };

  return (
    <View style={[styles.screen, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: '',
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={loading || fetching}
        LoadingComponent={
          <View style={styles.loaderStyle}>
            <LoadingSpinner />
          </View>
        }
        ErrorComponent={errorModel ? <ErrorScreen {...errorModel} /> : null}
      />
      <ActionScreen
        visual="CARD_A_ANIMATED"
        titleText={translatedText('howWorks.title')}
        titleTextStyle="extra-large"
        bodyContent={
          <View style={styles.outerContainer}>
            <View style={styles.stepCheckListContainer}>
              <StepChecklist
                stepperHeading=""
                stepperStates={[
                  {
                    content: {
                      annotation: (
                        <BodySmText style={styles.descriptionStyle}>
                          {translatedText('howWorks.steps.stepOne.description')}
                        </BodySmText>
                      ),
                      descriptionText: translatedText(
                        'howWorks.steps.stepOne.title',
                      ),
                    },
                    id: 'step1',
                    indicator: {
                      icon: 'CIRCLE',
                      tintColor: theme.color.interactive.primaryHovered,
                    },
                    stepState: 'complete',
                  },
                  {
                    content: {
                      annotation: (
                        <BodySmText style={styles.descriptionStyle}>
                          {translatedText('howWorks.steps.stepTwo.description')}
                        </BodySmText>
                      ),
                      descriptionText: translatedText(
                        'howWorks.steps.stepTwo.title',
                      ),
                    },
                    id: 'step2',
                    indicator: {
                      icon: 'CIRCLE',
                      tintColor: theme.color.interactive.primaryHovered,
                    },
                    stepState: 'complete',
                  },
                  {
                    content: {
                      annotation: (
                        <BodySmText style={styles.descriptionStyle}>
                          {translatedText(
                            'howWorks.steps.stepThree.description',
                          )}
                          {!isEnabled ? (
                            <HyperlinkSmText
                              numberOfLines={1}
                              onPress={handleLinkText}
                            >
                              {translatedText('howWorks.linkText')}
                            </HyperlinkSmText>
                          ) : null}
                        </BodySmText>
                      ),
                      descriptionText: translatedText(
                        'howWorks.steps.stepThree.title',
                      ),
                    },
                    id: 'step3',
                    indicator: {
                      icon: 'CIRCLE',
                      tintColor: theme.color.interactive.primaryHovered,
                    },
                    stepState: 'complete',
                  },
                  {
                    content: {
                      annotation: (
                        <BodySmText style={styles.descriptionStyle}>
                          {translatedText(
                            'howWorks.steps.stepFour.description',
                          )}
                        </BodySmText>
                      ),
                      descriptionText: translatedText(
                        'howWorks.steps.stepFour.title',
                      ),
                    },
                    id: 'step4',
                    indicator: {
                      icon: 'CIRCLE',
                      tintColor: theme.color.interactive.primaryHovered,
                    },
                    stepState: 'complete',
                  },
                ]}
              />
            </View>
            {!isEnabled ? (
              <View>
                <BottomSheet ref={bottomSheetRef}>
                  <View style={styles.bottomSheetOuterContainer}>
                    <TitleMdText numberOfLines={1}>
                      {translatedText('howWorks.bottomSheet.title')}
                    </TitleMdText>
                    <BodyBaseText style={styles.subTitle} numberOfLines={1}>
                      {translatedText('howWorks.bottomSheet.description')}
                    </BodyBaseText>
                  </View>
                  <View style={styles.bottomSheetButtonContainer}>
                    <Button
                      label={translatedText(
                        'howWorks.bottomSheet.primaryButton',
                      )}
                      onPress={handleTurnSafePayOn}
                      size="LARGE"
                      type="PRIMARY"
                      width="HUG"
                    />
                  </View>
                </BottomSheet>
              </View>
            ) : null}
          </View>
        }
      />
      {isEnabled && buttonClicked ? (
        <Snackbar
          text={translatedText('howToBuildCredit.snackBarString.text')}
          buttonOnPress="CLOSE"
          duration={SNACKBAR_DURATION}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  bottomSheetButtonContainer: {
    padding: theme.spacing.m,
  },
  bottomSheetOuterContainer: { padding: theme.spacing.m },
  descriptionStyle: {
    color: theme.color.text.secondary,
  },
  loaderStyle: { height: theme.spacing.l, width: theme.spacing.l },
  outerContainer: {
    borderColor: theme.color.border.primary,
    borderRadius: theme.spacing.xs,
    borderWidth: 1,
    flex: 1,
  },
  screen: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  stepCheckListContainer: {
    borderColor: theme.color.border.primarySolid,
  },
  subTitle: {
    marginTop: theme.spacing.s,
  },
});

export default HowBelieveWorksScreen;
